import React, {useContext, useMemo} from 'react';
import {DateTime} from "luxon";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendarDays, faClock, faImage} from "@fortawesome/free-solid-svg-icons";
import {AppContext, SpeciesFamilyLink} from "../App";
import {ObservationCard} from "./observation-card";
import {getMacaulayAssetUrl, getMacaulayCatalogNumbers} from "../helpers/macaulayLibrary";
import './infobox-species-location-detail.scss';

export type InfoboxSpeciesLocationDetailProps = {
    taxonomicOrder: number,
    observations?: any[],
    familyLink?: SpeciesFamilyLink,
    onClose?: () => void
}

const formatDate = (date: string) => {
    const dateTime = DateTime.fromISO(date);
    return dateTime.isValid ? dateTime.toFormat('LLL d, yyyy') : date;
}

const formatDateTime = (date: string) => {
    const dateTime = DateTime.fromISO(date);
    return dateTime.isValid ? dateTime.toFormat('LLL d, yyyy, h:mm a') : date;
}

const getObservationTime = (observation: any) => {
    if (Number.isFinite(observation?.properties?.dateMs)) {
        return observation.properties.dateMs;
    }

    const parsedDate = DateTime.fromISO(observation?.properties?.date);
    return parsedDate.isValid ? parsedDate.toMillis() : 0;
}

const getSightingLabel = (count: number) => `${count} ${count === 1 ? 'sighting' : 'sightings'} at this location`;

export function InfoboxSpeciesLocationDetail({
    taxonomicOrder,
    observations = [],
    familyLink,
    onClose
}: InfoboxSpeciesLocationDetailProps) {
    const appContext = useContext(AppContext);

    const sortedObservations = useMemo(() => {
        return [...observations].sort((a, b) => getObservationTime(b) - getObservationTime(a));
    }, [observations]);

    const primaryObservation = sortedObservations[0];
    const firstObservation = sortedObservations[sortedObservations.length - 1];
    const latestObservation = sortedObservations[0];
    const recentObservations = sortedObservations.slice(0, 5);
    const species = primaryObservation?.properties?.species;
    const location = primaryObservation?.properties?.locationName;
    const image = primaryObservation?.properties?.image;
    const hasLiferAtLocation = sortedObservations.some(observation => observation?.properties?.isLifer);
    const taxonomyResult = appContext.lookupByTaxonomicOrder(taxonomicOrder);
    const sourceUrl = taxonomyResult && familyLink
        ? `/family-explorer/family/${familyLink.familySlug}/species/${taxonomyResult.speciesCode}`
        : undefined;
    const recentPhotos = recentObservations.flatMap(observation => {
        return getMacaulayCatalogNumbers(observation.properties.mlCatalogNumbers).map(catalogNumber => ({
            catalogNumber
        }));
    });
    if (!primaryObservation) {
        return null;
    }

    return (
        <div className="infobox-species-location-detail__card-wrap">
            <ObservationCard
                variant="location-detail"
                image={image}
                kicker={getSightingLabel(sortedObservations.length)}
                title={species}
                location={location}
                badges={hasLiferAtLocation ? ['Lifer'] : []}
                familyLink={familyLink ? {
                    label: familyLink.familyName,
                    to: `/family-explorer/family/${familyLink.familySlug}`
                } : undefined}
                sourceUrl={sourceUrl}
                sourceLabel="Species Detail"
                onClose={onClose}
                closeLabel="Close location details"
                detail={
                    <>
                        <dl className="observation-card__stats">
                            {firstObservation ?
                                <div className="observation-card__stat">
                                    <dt>
                                        <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
                                        <span>First sighting</span>
                                    </dt>
                                    <dd>{formatDate(firstObservation.properties.date)}</dd>
                                </div>
                                : null
                            }
                            {latestObservation && latestObservation !== firstObservation ?
                                <div className="observation-card__stat">
                                    <dt>
                                        <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                                        <span>Most recent</span>
                                    </dt>
                                    <dd>{formatDate(latestObservation.properties.date)}</dd>
                                </div>
                                : null
                            }
                        </dl>

                        {recentObservations.length ?
                            <section className="observation-card__recent" aria-labelledby="species-location-recent-title">
                                <h3 id="species-location-recent-title">Recent Sightings</h3>
                                <ol>
                                    {recentObservations.map((observation, index) => (
                                        <li key={`${observation.properties.date}-${index}`}>
                                            <span>{formatDateTime(observation.properties.date)}</span>
                                            {observation.properties.count ?
                                                <span className="observation-card__recent-count">
                                                    Count: {observation.properties.count}
                                                </span>
                                                : null
                                            }
                                        </li>
                                    ))}
                                </ol>
                                {recentPhotos.length ?
                                    <div className="observation-card__photos">
                                        <h4>Photos</h4>
                                        <ul>
                                            {recentPhotos.map((photo, index) => (
                                                <li key={`${photo.catalogNumber}-${index}`}>
                                                    <a
                                                        href={getMacaulayAssetUrl(photo.catalogNumber)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        <FontAwesomeIcon icon={faImage} aria-hidden="true" />
                                                        <span>ML{photo.catalogNumber}</span>
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    : null
                                }
                            </section>
                            : null
                        }
                    </>
                }
            />
        </div>
    )
}
